import React, { useState } from 'react';
import { LayoutIcon, FilterIcon, DownloadIcon, SettingsIcon, AlertTriangleIcon, CheckCircleIcon, PlayIcon, BookOpenIcon, BotIcon, TrendingUpIcon, TrendingDownIcon, GripVerticalIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DndContext, DragOverlay, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
interface Program {
  id: string;
  name: string;
  owner: string;
  ownerAvatar: string;
  lane: 'acquire' | 'close' | 'expand';
  stage: string;
  segment: string;
  motion: string[];
  entries: number;
  exits: number;
  medianDays: number;
  onPace: boolean;
  pacingDelta: number;
  playbookCount: number;
  orchestrationCount: number;
  agentAttached: boolean;
  health: 'healthy' | 'at-risk' | 'critical';
  alertCount: number;
}
function ProgramCard({
  program,
  isDragging
}: {
  program: Program;
  isDragging?: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition
  } = useSortable({
    id: program.id
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1
  };
  const getLaneColor = (lane: string) => {
    switch (lane) {
      case 'acquire':
        return 'bg-blue-100 text-blue-700';
      case 'close':
        return 'bg-green-100 text-green-700';
      case 'expand':
        return 'bg-purple-100 text-purple-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy':
        return 'text-green-600';
      case 'at-risk':
        return 'text-yellow-600';
      case 'critical':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };
  return <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-all cursor-move" data-id="element-5330">
      {/* Header */}
      <div className="flex items-start justify-between mb-2" data-id="element-5331">
        <div className="flex-1 min-w-0" data-id="element-5332">
          <div className="flex items-center gap-2 mb-1" data-id="element-5333">
            <GripVerticalIcon className="w-3 h-3 text-gray-400 flex-shrink-0" data-id="element-5334" />
            <h4 className="text-sm font-semibold text-gray-900 truncate" data-id="element-5335">
              {program.name}
            </h4>
          </div>
          <div className="flex items-center gap-2" data-id="element-5336">
            <div className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium text-gray-600" data-id="element-5337">
              {program.ownerAvatar}
            </div>
            <span className="text-xs text-gray-600 truncate" data-id="element-5338">
              {program.owner}
            </span>
          </div>
        </div>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getLaneColor(program.lane)}`} data-id="element-5339">
          {program.lane}
        </span>
      </div>
      {/* KPIs */}
      <div className="grid grid-cols-3 gap-2 mb-2 text-center" data-id="element-5340">
        <div data-id="element-5341">
          <p className="text-xs text-gray-600" data-id="element-5342">In</p>
          <p className="text-sm font-bold text-gray-900" data-id="element-5343">{program.entries}</p>
        </div>
        <div data-id="element-5344">
          <p className="text-xs text-gray-600" data-id="element-5345">Out</p>
          <p className="text-sm font-bold text-gray-900" data-id="element-5346">{program.exits}</p>
        </div>
        <div data-id="element-5347">
          <p className="text-xs text-gray-600" data-id="element-5348">Days</p>
          <p className="text-sm font-bold text-gray-900" data-id="element-5349">
            {program.medianDays}
          </p>
        </div>
      </div>
      {/* Pacing Badge */}
      <div className={`flex items-center justify-center gap-1 px-2 py-1 rounded mb-2 ${program.onPace ? 'bg-green-50' : 'bg-red-50'}`} data-id="element-5350">
        {program.onPace ? <TrendingUpIcon className="w-3 h-3 text-green-600" data-id="element-5351" /> : <TrendingDownIcon className="w-3 h-3 text-red-600" data-id="element-5352" />}
        <span className={`text-xs font-medium ${program.onPace ? 'text-green-700' : 'text-red-700'}`} data-id="element-5353">
          {program.onPace ? 'On pace' : `${Math.abs(program.pacingDelta)}% behind`}
        </span>
      </div>
      {/* Counts and Badges */}
      <div className="flex items-center justify-between" data-id="element-5354">
        <div className="flex items-center gap-3 text-xs text-gray-600" data-id="element-5355">
          <div className="flex items-center gap-1" data-id="element-5356">
            <BookOpenIcon className="w-3 h-3" data-id="element-5357" />
            <span data-id="element-5358">{program.playbookCount}</span>
          </div>
          <div className="flex items-center gap-1" data-id="element-5359">
            <PlayIcon className="w-3 h-3" data-id="element-5360" />
            <span data-id="element-5361">{program.orchestrationCount}</span>
          </div>
          {program.agentAttached && <div className="flex items-center gap-1 text-purple-600" data-id="element-5362">
              <BotIcon className="w-3 h-3" data-id="element-5363" />
            </div>}
        </div>
        <div className="flex items-center gap-1" data-id="element-5364">
          {program.health === 'healthy' ? <CheckCircleIcon className={`w-4 h-4 ${getHealthColor(program.health)}`} data-id="element-5365" /> : <AlertTriangleIcon className={`w-4 h-4 ${getHealthColor(program.health)}`} data-id="element-5366" />}
          {program.alertCount > 0 && <span className="w-4 h-4 bg-red-600 text-white text-xs font-bold rounded-full flex items-center justify-center" data-id="element-5367">
              {program.alertCount}
            </span>}
        </div>
      </div>
    </div>;
}
export function OrchestrationKanbanPage() {
  const navigate = useNavigate();
  const [planningMode, setPlanningMode] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [groupBy, setGroupBy] = useState<'none' | 'segment' | 'motion'>('none');
  const [filters, setFilters] = useState({
    onlyAtRisk: false,
    hasAgent: false,
    overWIP: false,
    myPrograms: false
  });
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor));
  // Stage configuration
  const stages = [{
    key: 'awareness',
    label: 'Awareness',
    lane: 'acquire',
    wipLimit: null
  }, {
    key: 'education',
    label: 'Education',
    lane: 'acquire',
    wipLimit: null
  }, {
    key: 'consideration',
    label: 'Consideration',
    lane: 'acquire',
    wipLimit: null
  }, {
    key: 'intent',
    label: 'Intent',
    lane: 'acquire',
    wipLimit: null
  }, {
    key: 'selection',
    label: 'Selection',
    lane: 'acquire',
    wipLimit: null
  }, {
    key: 'evaluation',
    label: 'Evaluation',
    lane: 'close',
    wipLimit: 50
  }, {
    key: 'negotiation',
    label: 'Negotiation',
    lane: 'close',
    wipLimit: 30
  }, {
    key: 'commit',
    label: 'Commit',
    lane: 'close',
    wipLimit: 20
  }, {
    key: 'onboarding',
    label: 'Onboarding',
    lane: 'expand',
    wipLimit: null
  }, {
    key: 'activation',
    label: 'Activation',
    lane: 'expand',
    wipLimit: null
  }, {
    key: 'impact',
    label: 'Impact',
    lane: 'expand',
    wipLimit: null
  }, {
    key: 'expansion',
    label: 'Expansion',
    lane: 'expand',
    wipLimit: null
  }, {
    key: 'growth',
    label: 'Growth',
    lane: 'expand',
    wipLimit: null
  }];
  // Seeded programs (18 total)
  const [programs, setPrograms] = useState<Program[]>([{
    id: 'prog-1',
    name: 'Enterprise ABM Q1',
    owner: 'Sarah Chen',
    ownerAvatar: 'SC',
    lane: 'acquire',
    stage: 'awareness',
    segment: 'Enterprise',
    motion: ['ABM', 'Outbound'],
    entries: 342,
    exits: 289,
    medianDays: 8,
    onPace: true,
    pacingDelta: 0,
    playbookCount: 3,
    orchestrationCount: 8,
    agentAttached: true,
    health: 'healthy',
    alertCount: 0
  }, {
    id: 'prog-2',
    name: 'PLG Trial Conversion',
    owner: 'Mike Rodriguez',
    ownerAvatar: 'MR',
    lane: 'acquire',
    stage: 'awareness',
    segment: 'SMB',
    motion: ['PLG', 'Inbound'],
    entries: 1234,
    exits: 987,
    medianDays: 6,
    onPace: true,
    pacingDelta: 0,
    playbookCount: 2,
    orchestrationCount: 5,
    agentAttached: true,
    health: 'healthy',
    alertCount: 0
  }, {
    id: 'prog-3',
    name: 'Partner Co-Marketing',
    owner: 'Lisa Park',
    ownerAvatar: 'LP',
    lane: 'acquire',
    stage: 'education',
    segment: 'Mid-Market',
    motion: ['Partner', 'Inbound'],
    entries: 456,
    exits: 378,
    medianDays: 11,
    onPace: false,
    pacingDelta: -12,
    playbookCount: 2,
    orchestrationCount: 4,
    agentAttached: false,
    health: 'at-risk',
    alertCount: 2
  }, {
    id: 'prog-4',
    name: 'Field Event Pipeline',
    owner: 'John Smith',
    ownerAvatar: 'JS',
    lane: 'acquire',
    stage: 'education',
    segment: 'Enterprise',
    motion: ['Field', 'Outbound'],
    entries: 234,
    exits: 189,
    medianDays: 13,
    onPace: true,
    pacingDelta: 0,
    playbookCount: 3,
    orchestrationCount: 6,
    agentAttached: true,
    health: 'healthy',
    alertCount: 0
  }, {
    id: 'prog-5',
    name: 'Inbound Lead Nurture',
    owner: 'Emma Davis',
    ownerAvatar: 'ED',
    lane: 'acquire',
    stage: 'consideration',
    segment: 'SMB',
    motion: ['Inbound'],
    entries: 789,
    exits: 623,
    medianDays: 15,
    onPace: false,
    pacingDelta: -8,
    playbookCount: 2,
    orchestrationCount: 7,
    agentAttached: true,
    health: 'at-risk',
    alertCount: 1
  }, {
    id: 'prog-6',
    name: 'Competitive Displacement',
    owner: 'David Kim',
    ownerAvatar: 'DK',
    lane: 'acquire',
    stage: 'consideration',
    segment: 'Mid-Market',
    motion: ['Outbound', 'ABM'],
    entries: 167,
    exits: 134,
    medianDays: 18,
    onPace: true,
    pacingDelta: 0,
    playbookCount: 4,
    orchestrationCount: 9,
    agentAttached: true,
    health: 'healthy',
    alertCount: 0
  }, {
    id: 'prog-7',
    name: 'Intent Signal Response',
    owner: 'Sarah Chen',
    ownerAvatar: 'SC',
    lane: 'acquire',
    stage: 'intent',
    segment: 'Enterprise',
    motion: ['ABM', 'Outbound'],
    entries: 234,
    exits: 198,
    medianDays: 9,
    onPace: true,
    pacingDelta: 0,
    playbookCount: 2,
    orchestrationCount: 5,
    agentAttached: true,
    health: 'healthy',
    alertCount: 0
  }, {
    id: 'prog-8',
    name: 'Product-Led Sales',
    owner: 'Mike Rodriguez',
    ownerAvatar: 'MR',
    lane: 'acquire',
    stage: 'intent',
    segment: 'SMB',
    motion: ['PLG'],
    entries: 567,
    exits: 478,
    medianDays: 7,
    onPace: true,
    pacingDelta: 0,
    playbookCount: 2,
    orchestrationCount: 4,
    agentAttached: false,
    health: 'healthy',
    alertCount: 0
  }, {
    id: 'prog-9',
    name: 'Executive Engagement',
    owner: 'Lisa Park',
    ownerAvatar: 'LP',
    lane: 'close',
    stage: 'evaluation',
    segment: 'Enterprise',
    motion: ['ABM', 'Field'],
    entries: 89,
    exits: 67,
    medianDays: 23,
    onPace: false,
    pacingDelta: -15,
    playbookCount: 3,
    orchestrationCount: 7,
    agentAttached: true,
    health: 'critical',
    alertCount: 3
  }, {
    id: 'prog-10',
    name: 'POV Acceleration',
    owner: 'John Smith',
    ownerAvatar: 'JS',
    lane: 'close',
    stage: 'evaluation',
    segment: 'Mid-Market',
    motion: ['Outbound'],
    entries: 134,
    exits: 98,
    medianDays: 21,
    onPace: true,
    pacingDelta: 0,
    playbookCount: 2,
    orchestrationCount: 5,
    agentAttached: false,
    health: 'healthy',
    alertCount: 0
  }, {
    id: 'prog-11',
    name: 'Legal Review Fast Track',
    owner: 'Emma Davis',
    ownerAvatar: 'ED',
    lane: 'close',
    stage: 'negotiation',
    segment: 'Enterprise',
    motion: ['ABM'],
    entries: 45,
    exits: 34,
    medianDays: 26,
    onPace: false,
    pacingDelta: -20,
    playbookCount: 2,
    orchestrationCount: 4,
    agentAttached: true,
    health: 'critical',
    alertCount: 4
  }, {
    id: 'prog-12',
    name: 'Business Case Builder',
    owner: 'David Kim',
    ownerAvatar: 'DK',
    lane: 'close',
    stage: 'negotiation',
    segment: 'Mid-Market',
    motion: ['Outbound'],
    entries: 67,
    exits: 52,
    medianDays: 19,
    onPace: true,
    pacingDelta: 0,
    playbookCount: 2,
    orchestrationCount: 3,
    agentAttached: false,
    health: 'healthy',
    alertCount: 0
  }, {
    id: 'prog-13',
    name: 'Contract Acceleration',
    owner: 'Sarah Chen',
    ownerAvatar: 'SC',
    lane: 'close',
    stage: 'commit',
    segment: 'Enterprise',
    motion: ['ABM'],
    entries: 34,
    exits: 28,
    medianDays: 14,
    onPace: true,
    pacingDelta: 0,
    playbookCount: 1,
    orchestrationCount: 3,
    agentAttached: false,
    health: 'healthy',
    alertCount: 0
  }, {
    id: 'prog-14',
    name: 'Welcome & Onboarding',
    owner: 'Mike Rodriguez',
    ownerAvatar: 'MR',
    lane: 'expand',
    stage: 'onboarding',
    segment: 'All',
    motion: ['PLG'],
    entries: 156,
    exits: 145,
    medianDays: 32,
    onPace: true,
    pacingDelta: 0,
    playbookCount: 2,
    orchestrationCount: 5,
    agentAttached: true,
    health: 'healthy',
    alertCount: 0
  }, {
    id: 'prog-15',
    name: 'Feature Adoption',
    owner: 'Lisa Park',
    ownerAvatar: 'LP',
    lane: 'expand',
    stage: 'activation',
    segment: 'All',
    motion: ['PLG'],
    entries: 234,
    exits: 212,
    medianDays: 48,
    onPace: true,
    pacingDelta: 0,
    playbookCount: 3,
    orchestrationCount: 8,
    agentAttached: true,
    health: 'healthy',
    alertCount: 0
  }, {
    id: 'prog-16',
    name: 'Value Realization',
    owner: 'John Smith',
    ownerAvatar: 'JS',
    lane: 'expand',
    stage: 'impact',
    segment: 'Enterprise',
    motion: ['Field'],
    entries: 89,
    exits: 78,
    medianDays: 65,
    onPace: true,
    pacingDelta: 0,
    playbookCount: 2,
    orchestrationCount: 4,
    agentAttached: false,
    health: 'healthy',
    alertCount: 0
  }, {
    id: 'prog-17',
    name: 'Upsell & Cross-sell',
    owner: 'Emma Davis',
    ownerAvatar: 'ED',
    lane: 'expand',
    stage: 'expansion',
    segment: 'Mid-Market',
    motion: ['Outbound'],
    entries: 123,
    exits: 98,
    medianDays: 102,
    onPace: false,
    pacingDelta: -10,
    playbookCount: 3,
    orchestrationCount: 6,
    agentAttached: true,
    health: 'at-risk',
    alertCount: 2
  }, {
    id: 'prog-18',
    name: 'Strategic Account',
    owner: 'David Kim',
    ownerAvatar: 'DK',
    lane: 'expand',
    stage: 'growth',
    segment: 'Enterprise',
    motion: ['ABM', 'Field'],
    entries: 45,
    exits: 42,
    medianDays: 195,
    onPace: true,
    pacingDelta: 0,
    playbookCount: 2,
    orchestrationCount: 5,
    agentAttached: false,
    health: 'healthy',
    alertCount: 0
  }]);
  const handleDragStart = (event: any) => {
    if (!planningMode) return;
    setActiveId(event.active.id);
  };
  const handleDragEnd = (event: any) => {
    if (!planningMode) return;
    const {
      active,
      over
    } = event;
    if (!over) return;
    const activeProgram = programs.find(p => p.id === active.id);
    const newStage = over.id;
    if (activeProgram && activeProgram.stage !== newStage) {
      // Show planning drawer (simplified for now)
      console.log(`Planning to move ${activeProgram.name} from ${activeProgram.stage} to ${newStage}`);
      // Update program stage
      setPrograms(prev => prev.map(p => p.id === active.id ? {
        ...p,
        stage: newStage
      } : p));
    }
    setActiveId(null);
  };
  const togglePlanningMode = () => {
    if (!planningMode) {
      setShowConfirmation(true);
    } else {
      setPlanningMode(false);
    }
  };
  const confirmPlanningMode = () => {
    setPlanningMode(true);
    setShowConfirmation(false);
  };
  const getProgramsByStage = (stageKey: string) => {
    return programs.filter(p => p.stage === stageKey);
  };
  const getStageAggregates = (stageKey: string) => {
    const stagePrograms = getProgramsByStage(stageKey);
    return {
      totalEntries: stagePrograms.reduce((sum, p) => sum + p.entries, 0),
      totalExits: stagePrograms.reduce((sum, p) => sum + p.exits, 0),
      programCount: stagePrograms.length
    };
  };
  return <div className="w-full h-screen flex flex-col bg-gray-50" data-id="element-5368">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4" data-id="element-5369">
        <div className="flex items-center justify-between mb-4" data-id="element-5370">
          <div data-id="element-5371">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5372">Kanban View</h1>
            <p className="text-sm text-gray-600 mt-1" data-id="element-5373">
              Program-first board with drag-and-drop planning
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-5374">
            <button onClick={() => navigate('/orchestration/journey-map')} className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5375">
              <LayoutIcon className="w-4 h-4" data-id="element-5376" />
              Map View
            </button>
            <button onClick={togglePlanningMode} className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${planningMode ? 'bg-purple-600 text-white hover:bg-purple-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-5377">
              {planningMode ? 'Exit Planning' : 'Planning Mode'}
            </button>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5378">
              <FilterIcon className="w-4 h-4" data-id="element-5379" />
              Filters
            </button>
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5380">
              <DownloadIcon className="w-4 h-4" data-id="element-5381" />
              Export
            </button>
          </div>
        </div>
        {/* Filters and Grouping */}
        <div className="flex items-center gap-3" data-id="element-5382">
          <select value={groupBy} onChange={e => setGroupBy(e.target.value as any)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" data-id="element-5383">
            <option value="none" data-id="element-5384">No grouping</option>
            <option value="segment" data-id="element-5385">Group by Segment</option>
            <option value="motion" data-id="element-5386">Group by Motion</option>
          </select>
          <div className="flex items-center gap-2" data-id="element-5387">
            <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5388">
              <input type="checkbox" checked={filters.onlyAtRisk} onChange={e => setFilters({
              ...filters,
              onlyAtRisk: e.target.checked
            })} className="rounded" data-id="element-5389" />
              Only at risk
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5390">
              <input type="checkbox" checked={filters.hasAgent} onChange={e => setFilters({
              ...filters,
              hasAgent: e.target.checked
            })} className="rounded" data-id="element-5391" />
              Has agent
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-5392">
              <input type="checkbox" checked={filters.myPrograms} onChange={e => setFilters({
              ...filters,
              myPrograms: e.target.checked
            })} className="rounded" data-id="element-5393" />
              My programs
            </label>
          </div>
        </div>
        {planningMode && <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg" data-id="element-5394">
            <p className="text-sm text-purple-900" data-id="element-5395">
              <strong data-id="element-5396">Planning Mode Active:</strong> Drag programs between
              stages to plan changes. Changes are saved as plans and do not
              affect production data.
            </p>
          </div>}
      </div>
      {/* Kanban Board */}
      <div className="flex-1 overflow-x-auto p-6" data-id="element-5397">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd} data-id="element-5398">
          <div className="flex gap-4 min-w-max" data-id="element-5399">
            {stages.map(stage => {
            const aggregates = getStageAggregates(stage.key);
            const stagePrograms = getProgramsByStage(stage.key);
            const isOverWIP = stage.wipLimit && aggregates.programCount > stage.wipLimit;
            return <div key={stage.key} className="w-80 flex-shrink-0" data-id="element-5400">
                  {/* Column Header */}
                  <div className={`p-3 rounded-t-lg border-b-2 ${stage.lane === 'acquire' ? 'bg-blue-50 border-blue-500' : stage.lane === 'close' ? 'bg-green-50 border-green-500' : 'bg-purple-50 border-purple-500'}`} data-id="element-5401">
                    <div className="flex items-center justify-between mb-2" data-id="element-5402">
                      <h3 className="font-semibold text-gray-900" data-id="element-5403">
                        {stage.label}
                      </h3>
                      <span className="px-2 py-0.5 bg-white rounded text-xs font-medium text-gray-700" data-id="element-5404">
                        {aggregates.programCount}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-600" data-id="element-5405">
                      <span data-id="element-5406">↓ {aggregates.totalEntries}</span>
                      <span data-id="element-5407">↑ {aggregates.totalExits}</span>
                    </div>
                    {stage.wipLimit && <div className={`mt-2 text-xs font-medium ${isOverWIP ? 'text-red-600' : 'text-gray-600'}`} data-id="element-5408">
                        WIP: {aggregates.programCount} / {stage.wipLimit}
                        {isOverWIP && ' (Over limit!)'}
                      </div>}
                  </div>
                  {/* Column Body */}
                  <SortableContext items={stagePrograms.map(p => p.id)} strategy={verticalListSortingStrategy} data-id="element-5409">
                    <div className="bg-gray-100 p-3 rounded-b-lg min-h-[400px] space-y-3" data-id="element-5410">
                      {stagePrograms.map(program => <ProgramCard key={program.id} program={program} data-id="element-5411" />)}
                    </div>
                  </SortableContext>
                </div>;
          })}
          </div>
          <DragOverlay data-id="element-5412">
            {activeId ? <ProgramCard program={programs.find(p => p.id === activeId)!} isDragging data-id="element-5413" /> : null}
          </DragOverlay>
        </DndContext>
      </div>
      {/* Planning Mode Confirmation Modal */}
      {showConfirmation && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-5414">
          <div className="bg-white rounded-lg p-6 max-w-md" data-id="element-5415">
            <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-5416">
              Enable Planning Mode?
            </h3>
            <p className="text-sm text-gray-600 mb-4" data-id="element-5417">
              Planning Mode allows you to drag programs between stages to create
              "what-if" scenarios. Changes are saved as plans and will not
              affect production data until explicitly approved.
            </p>
            <div className="flex items-center gap-3" data-id="element-5418">
              <button onClick={confirmPlanningMode} className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors" data-id="element-5419">
                Enable Planning
              </button>
              <button onClick={() => setShowConfirmation(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5420">
                Cancel
              </button>
            </div>
          </div>
        </div>}
    </div>;
}